/**
 * The chat composer's send shortcut.
 *
 * On a desktop keyboard Enter sends and Shift+Enter inserts a newline. On a
 * phone or any touch keyboard there is no Shift+Enter, so there a plain
 * Enter inserts a newline and the message goes out with the send button.
 * ⌘/Ctrl+Enter sends everywhere; *both* modifiers are accepted (a Mac with
 * an external PC keyboard, a Linux box with an Apple keyboard) and only the
 * hint we show follows the platform.
 */

export interface KeyboardInfo {
  /** The phone layout (`useIsMobile`). */
  phone: boolean
  /** `(pointer: coarse)` — a touch device, whose on-screen keyboard has no Shift+Enter. */
  coarsePointer: boolean
}

/** Whether a plain Enter sends (desktop) or inserts a newline (touch keyboards). Pure. */
export function enterSends(info: KeyboardInfo): boolean {
  return !info.phone && !info.coarsePointer
}

export interface PlatformInfo {
  /** `navigator.userAgentData?.platform`, else `navigator.platform`. */
  platform?: string | null
  /** `navigator.userAgent` — the fallback when the platform is empty. */
  userAgent?: string | null
}

export interface SendShortcut {
  /** The modifier shown in the hint (not the only one accepted). */
  key: 'meta' | 'ctrl'
  label: '⌘ Enter' | 'Ctrl+Enter'
}

/** Matches "macOS"/"MacIntel" as well as iPhone/iPad/iPod, in either field. */
const APPLE = /mac|iphone|ipad|ipod/i

/** The shortcut to *display* for a platform. Pure — the browser lookup is `currentPlatformInfo`. */
export function sendShortcut(info: PlatformInfo): SendShortcut {
  const platform = info.platform?.trim()
  const source = platform && platform.length > 0 ? platform : (info.userAgent ?? '')
  return APPLE.test(source)
    ? { key: 'meta', label: '⌘ Enter' }
    : { key: 'ctrl', label: 'Ctrl+Enter' }
}

/** Reads the platform from the browser; empty (→ Ctrl+Enter) outside one. */
export function currentPlatformInfo(): PlatformInfo {
  if (typeof navigator === 'undefined') return {}
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } }
  return { platform: nav.userAgentData?.platform ?? nav.platform, userAgent: nav.userAgent }
}
